import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Container from "../components/layout/Container";
import { fetchProducts, fetchCategories } from "../services/api";
import ProductCard from "../components/product/ProductCard";
import { useLanguage } from "../contexts/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

// Loading Skeleton Component
const ProductSkeleton = () => (
  <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg animate-pulse border border-white/20">
    <div className="bg-gradient-to-br from-slate-200 to-slate-300 h-48 lg:h-64 rounded-t-2xl"></div>
    <div className="p-6 space-y-4">
      <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-3/4"></div>
      <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-1/2"></div>
      <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-6 rounded-full w-1/3"></div>
    </div>
  </div>
);

// Error State Component
const ErrorState = ({ error, t }) => (
  <div className="text-center py-20">
    <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-12 max-w-lg mx-auto border border-red-200/50 shadow-xl">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-red-700 mb-4">{t('common.error') || 'Xatolik yuz berdi'}</h3>
      <p className="text-red-600 mb-8">{error}</p>
      <Link 
        to="/" 
        className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
      >
        <span className="mr-2">{t('pages.products.backHome') || 'Bosh Sahifaga qaytish'}</span>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      </Link>
    </div>
  </div>
);

// Empty State Component
const EmptyState = ({ search, selectedCategory, categories, onClearFilters, t }) => {
  const selectedCategoryName = selectedCategory 
    ? categories.find(cat => cat.id === selectedCategory)?.name 
    : null;

  return (
    <div className="text-center py-20">
      <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-12 max-w-lg mx-auto border border-slate-200/50 shadow-xl">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-4">{t('pages.products.noProducts') || 'Mahsulot topilmadi'}</h3>
        <div className="text-slate-600 mb-8 space-y-2">
          {search && (
            <p>"<span className="font-medium">{search}</span>" {t('pages.products.searchQuery') || "so'rovi bo'yicha"}</p>
          )}
          {selectedCategoryName && (
            <p>"<span className="font-medium">{selectedCategoryName}</span>" {t('pages.products.inCategory') || 'kategoriyasida'}</p>
          )}
          <p className="text-sm">{t('pages.products.noProductsDetails') || 'hech qanday mahsulot topilmadi.'}</p>
        </div>
        <button
          onClick={onClearFilters}
          className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <span className="mr-2">{t('pages.products.clearFilters') || 'Filtrlarni tozalash'}</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Filter Controls Component
const FilterControls = ({ 
  search, 
  setSearch, 
  selectedCategory, 
  setSelectedCategory, 
  sort, 
  setSort, 
  categories, 
  showFilters, 
  setShowFilters, 
  t 
}) => (
  <div className="bg-gradient-to-r from-white/90 via-white/95 to-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8 mb-12 relative overflow-hidden">
    {/* Background decoration */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
    
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
      {/* Search Bar - Enhanced */}
      <div className="relative flex-1 max-w-lg">
        <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-slate-400 z-10">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder={t('pages.products.searchPlaceholder') || 'Mahsulot qidirish...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-14 pr-12 py-4 border-2 border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 bg-white/90 backdrop-blur-sm transition-all duration-300 hover:bg-white hover:border-slate-300 shadow-lg hover:shadow-xl text-slate-800 placeholder-slate-400 font-medium"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full p-1 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Mobile Filter Toggle - Enhanced */}
      <div className="lg:hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl font-semibold transform hover:scale-105"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
          </svg>
          {t('pages.products.filters') || 'Filtrlar'}
          <svg className={`w-5 h-5 transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Desktop Filters - Enhanced */}
      <div className="hidden lg:flex items-center gap-6">
        <div className="relative">
          <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.category') || 'Kategoriya'}</label>
          <select
            value={selectedCategory || ""}
            onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
            className="px-5 py-4 border-2 border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 bg-white/90 backdrop-blur-sm transition-all duration-300 hover:bg-white hover:border-slate-300 shadow-lg hover:shadow-xl min-w-[220px] text-slate-800 font-medium"
          >
            <option value="">{t('pages.products.allCategories') || 'Barcha kategoriyalar'}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="relative">
          <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.sort') || 'Saralash'}</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-5 py-4 border-2 border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 bg-white/90 backdrop-blur-sm transition-all duration-300 hover:bg-white hover:border-slate-300 shadow-lg hover:shadow-xl min-w-[200px] text-slate-800 font-medium"
          >
            <option value="name">{t('pages.products.sortByName') || "Nom bo'yicha"}</option>
            <option value="price-asc">{t('pages.products.sortPriceAsc') || 'Narx: arzon → qimmat'}</option>
            <option value="price-desc">{t('pages.products.sortPriceDesc') || 'Narx: qimmat → arzon'}</option>
            <option value="newest">{t('pages.products.sortNewest') || 'Eng yangilari'}</option>
          </select>
        </div>
      </div>
    </div>

    {/* Mobile Filters - Enhanced */}
    {showFilters && (
      <div className="lg:hidden mt-8 pt-8 border-t-2 border-gradient-to-r border-slate-200 space-y-6 animate-fadeInDown">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">{t('pages.products.category') || 'Kategoriya'}</label>
          <select
            value={selectedCategory || ""}
            onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-5 py-4 border-2 border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 bg-white/90 backdrop-blur-sm transition-all duration-300 shadow-lg font-medium text-slate-800"
          >
            <option value="">{t('pages.products.allCategories') || 'Barcha kategoriyalar'}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">{t('pages.products.sort') || 'Saralash'}</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full px-5 py-4 border-2 border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 bg-white/90 backdrop-blur-sm transition-all duration-300 shadow-lg font-medium text-slate-800"
          >
            <option value="name">{t('pages.products.sortByName') || "Nom bo'yicha"}</option>
            <option value="price-asc">{t('pages.products.sortPriceAsc') || 'Narx: arzon → qimmat'}</option>
            <option value="price-desc">{t('pages.products.sortPriceDesc') || 'Narx: qimmat → arzon'}</option>
            <option value="newest">{t('pages.products.sortNewest') || 'Eng yangilari'}</option>
          </select>
        </div>
      </div>
    )}
  </div>
);

// Results Info Component
const ResultsInfo = ({ filteredCount, selectedCategory, categories, currentPage, productsPerPage, t }) => {
  const selectedCategoryName = selectedCategory 
    ? categories.find(cat => cat.id === selectedCategory)?.name 
    : null;

  const startIndex = (currentPage - 1) * productsPerPage + 1;
  const endIndex = Math.min(currentPage * productsPerPage, filteredCount);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
      <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/30 shadow-sm">
        <span className="font-bold text-slate-900 text-lg">{filteredCount}</span>
        <span className="text-slate-600 ml-2">{t('pages.products.productsFound') || 'mahsulot topildi'}</span>
        {selectedCategoryName && (
          <span className="text-slate-500 ml-2">
            "<span className="font-medium text-slate-700">{selectedCategoryName}</span>" {t('pages.products.inCategory') || 'kategoriyasida'}
          </span>
        )}
      </div>
      
      {filteredCount > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/30 shadow-sm mt-4 sm:mt-0">
          <span className="text-sm text-slate-600">
            {t('pages.products.pagination.showing') || 'Ko\'rsatilmoqda'} <span className="font-semibold">{startIndex}</span>-<span className="font-semibold">{endIndex}</span> {t('pages.products.pagination.of') || 'dan'} <span className="font-semibold">{filteredCount}</span>
          </span>
        </div>
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, t }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= Math.min(5, totalPages); i++) {
          pages.push(i);
        }
        if (totalPages > 5) {
          pages.push('...');
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        if (totalPages > 5) {
          pages.push('...');
        }
        for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex justify-center items-center mt-12">
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/30 p-4">
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              currentPage === 1
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:scale-105'
            }`}
          >
            {t('pages.products.pagination.previous') || 'Oldingi'}
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1 mx-4">
            {pageNumbers.map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' ? onPageChange(page) : null}
                disabled={typeof page !== 'number'}
                className={`min-w-[40px] h-10 rounded-xl font-semibold transition-all duration-200 ${
                  page === currentPage
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                    : typeof page === 'number'
                    ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 transform hover:scale-105'
                    : 'bg-transparent text-slate-400 cursor-default'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              currentPage === totalPages
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:scale-105'
            }`}
          >
            {t('pages.products.pagination.next') || 'Keyingi'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(9);
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();

  // Data fetching with improved error handling
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const loadData = async () => {
      try {
        setLoading(true);
        setError("");
        
        const [productsData, categoriesData] = await Promise.all([
          fetchProducts(currentLanguage),
          fetchCategories(currentLanguage).catch(() => [])
        ]);
        
        if (mounted) {
          setProducts(productsData || []);
          setCategories(categoriesData || []);
        }
      } catch (err) {
        console.error('Products loading error:', err);
        if (mounted && !controller.signal.aborted) {
          setError(err.message || 'Xatolik yuz berdi');
          setProducts([]);
          setCategories([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [currentLanguage]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      const matchesCategory = !selectedCategory || 
        (product.category && (product.category.id === selectedCategory || product.category === selectedCategory));
      
      const matchesSearch = !search || 
        (product.name && product.name.toLowerCase().includes(search.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(search.toLowerCase())) ||
        (product.category && product.category.name && product.category.name.toLowerCase().includes(search.toLowerCase()));

      return matchesCategory && matchesSearch;
    });

    // Sort products
    filtered.sort((a, b) => {
      switch (sort) {
        case "price-asc":
          const aPrice = Number(a.discount_price || a.price || 0);
          const bPrice = Number(b.discount_price || b.price || 0);
          return aPrice - bPrice;
        case "price-desc":
          const aPriceDesc = Number(a.discount_price || a.price || 0);
          const bPriceDesc = Number(b.discount_price || b.price || 0);
          return bPriceDesc - aPriceDesc;
        case "name":
          return (a.name || '').localeCompare(b.name || '');
        case "newest":
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, selectedCategory, search, sort]);

  const clearFilters = useCallback(() => {
    setSearch("");
    setSelectedCategory(null);
    setSort("name");
    setCurrentPage(1);
  }, []);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCategory, sort]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Container className="py-8">
          <div className="py-12 lg:py-20">
            {/* Header Skeleton */}
            <div className="text-center mb-16">
              <div className="inline-block bg-blue-100/80 rounded-full px-6 py-3 mb-6">
                <div className="w-20 h-4 bg-blue-200 rounded animate-pulse"></div>
              </div>
              <div className="w-80 h-12 bg-slate-200 rounded mx-auto mb-4 animate-pulse"></div>
              <div className="w-96 h-6 bg-slate-100 rounded mx-auto animate-pulse"></div>
            </div>
            
            {/* Products Grid Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }, (_, i) => (
                <ProductSkeleton key={i} />
              ))}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Container className="py-8">
          <ErrorState error={error} t={t} />
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <Container className="py-8">
        <div className="py-12 lg:py-20">
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 bg-clip-text text-transparent leading-tight">
              {t('pages.products.ourProducts') || 'Bizning Mahsulotlarimiz'}
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
              {t('pages.products.subtitle') || 'Yuqori sifatli va ishonchli mahsulotlarni kashf eting. Har bir mahsulot professional standartlarda tayyorlangan.'}
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>

          {/* Filter Controls */}
          <FilterControls
            search={search}
            setSearch={setSearch}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            sort={sort}
            setSort={setSort}
            categories={categories}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            t={t}
          />

          {/* Results Info */}
          <ResultsInfo 
            filteredCount={filteredProducts.length}
            selectedCategory={selectedCategory}
            categories={categories}
            currentPage={currentPage}
            productsPerPage={productsPerPage}
            t={t}
          />

          {/* Products Grid or Empty State */}
          {filteredProducts.length === 0 ? (
            <EmptyState
              search={search}
              selectedCategory={selectedCategory}
              categories={categories}
              onClearFilters={clearFilters}
              t={t}
            />
          ) : (
            <>
              {/* Products Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentProducts.map((product, index) => (
                  <div 
                    key={product.id} 
                    className="transform hover:scale-105 transition-all duration-300"
                    style={{ 
                      animationDelay: `${index * 0.1}s`,
                      animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                  >
                    <ProductCard product={product} showButton={true} />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                t={t}
              />
            </>
          )}

          {/* CTA Section */}
          {filteredProducts.length > 0 && (
            <div className="mt-20 relative bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 overflow-hidden">
              <div className="bg-gradient-to-r from-red-800 via-red-900 to-red-800 rounded-3xl p-12 lg:p-16 text-center text-white overflow-hidden shadow-2xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
              </div>
              
              <div className="relative z-10">
                <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                  {t('pages.products.ctaTitle') || 'Qiziqtirgan mahsulot topmadingizmi?'}
                </h2>
                <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto leading-relaxed">
                  {t('pages.products.ctaDescription') || "Biz bilan bog'laning va sizga mos mahsulotni birga topamiz. Professional konsultatsiya bepul!"}
                </p>
                <Link 
                  to="/contact"
                  className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <span className="mr-2">{t('common.contactUs') || "Biz bilan bog'lanish"}</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
              </div>
            </div>
          )}
        </div>
      </Container>
      
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInDown {
          animation: fadeInDown 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Products;
